import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

import { toOfferLink, toRedirectAfterLoginLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { stripQs } from 'lib/request';

import { useUser } from 'providers/UserProvider';

import useDialog from './useDialog';

import APP from 'constants/app';
import OFFER from 'constants/offer';

const ExclusiveForAppsAlertDialog = dynamic(
  () => import('components/ExclusiveForAppDialog'),
  { ssr: false }
);

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const formatGaEventActionByVisibility = (prefix, visibility) => {
  const postfixes = {
    [OFFER.VISIBILITY_STATUS.ALL]: '',
    [OFFER.VISIBILITY_STATUS.APP_ONLY]: '__app__exclusive',
    [OFFER.VISIBILITY_STATUS.LOGGED_ONLY]: '__exclusive',
    [OFFER.VISIBILITY_STATUS.UNLOGGED_ONLY]: '',
  };

  const postfix = postfixes[visibility] || '';

  return prefix + postfix;
};

const useExclusiveOffer = () => {
  const router = useRouter();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const { showDialog } = useDialog();

  const isOfferBlocked = useCallback(
    (visibility, offerSlug) => {
      /**
       * The visibility of `UNLOGGED_ONLY` already exists but your logic is not
       * implemented yet.
       */
      if (
        visibility === OFFER.VISIBILITY_STATUS.ALL ||
        visibility === OFFER.VISIBILITY_STATUS.UNLOGGED_ONLY
      ) {
        return false;
      }

      if (visibility === OFFER.VISIBILITY_STATUS.APP_ONLY) {
        return true;
      }

      /**
       * Due some API related issues we are manually modifying the behavior of
       * this specific offer, showing the login dialog when user is unlogged
       * and showing the download app dialog when user is logged.
       */
      if (offerSlug === OFFER.DOWNLOAD_APP_FIXED_OFFER_SLUG) {
        return true;
      }

      return !loadingLoggedInUser && !isLoggedIn;
    },
    [loadingLoggedInUser, isLoggedIn]
  );

  const onUnloggedUserTriesOpenExclusiveOffer = useCallback(
    (offerSlug, visibility) => {
      const currPathname = stripQs(router.asPath);
      const offerLink = toOfferLink(offerSlug);
      const isInOfferPage = currPathname === offerLink;

      /**
       * Due some API related issues we are manually modifying the behavior of
       * this specific offer, showing the login dialog when user is unlogged
       * and showing the download app dialog when user is logged.
       */
      if (isLoggedIn && offerSlug === OFFER.DOWNLOAD_APP_FIXED_OFFER_SLUG) {
        showDialog(ExclusiveForAppsAlertDialog, {
          customTitle: 'Economize até 3x mais com nosso app',
          domain: OFFER.DOMAIN,
          disableCloseWhenOverlayClick: isInOfferPage,
          qrCodeUrl: `${APP.LOCAL_IMAGE_PATH}/features/exclusive-app/fixed-offer-qr-code.svg`,
          downloadAppUrl: 'https://promobit.onelink.me/OPOZ/y5pssd1h',
        });
        return;
      }

      if (isLoggedIn && visibility === OFFER.VISIBILITY_STATUS.LOGGED_ONLY) {
        if (isInOfferPage) {
          return;
        }

        router.push(offerLink);
      }

      if (visibility === OFFER.VISIBILITY_STATUS.APP_ONLY) {
        showDialog(ExclusiveForAppsAlertDialog, {
          domain: OFFER.DOMAIN,
          disableCloseWhenOverlayClick: isInOfferPage,
        });
        return;
      }

      showDialog(LoginDialog, {
        greetingsMessage: placeholder('titles.exclusiveOfferSocialSignin'),
        disableCloseWhenOverlayClick: isInOfferPage,
        onSuccess: () => {
          if (isInOfferPage) {
            return;
          }

          const urlWithRedirect = toRedirectAfterLoginLink(
            currPathname,
            toOfferLink(offerSlug)
          );

          router.push(urlWithRedirect, undefined, { shallow: true });
        },
      });
    },
    [loadingLoggedInUser, isLoggedIn, router]
  );

  return {
    isOfferBlocked,
    formatGaEventActionByVisibility,
    onUnloggedUserTriesOpenExclusiveOffer,
  };
};

export default useExclusiveOffer;
